import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { SelectOption } from '../../../model/selectOption'
import {
  ProjectStatus,
  WbsItemCodeFormat,
} from '../../../../domain/entity/ProjectEntity'
import {
  ReferencedEntity,
  ReferencedEntityWithIcon,
  REFERENCED_ENTITY_SEARCH_LIMIT,
} from '../../../../domain/value-object/ReferencedEntity'
import { findDivisions } from '../../../../infrastructure/api/divisions'
import { findBusinessClients } from '../../../../infrastructure/api/business-clients'
import { findUsers } from '../../../../infrastructure/api/users'

// 取得済みの全件リストを名前で絞り込む（オートコンプリート方式）。
// 共通のエンティティ検索上限件数までに丸める。
const filterByName = <T extends ReferencedEntity>(
  list: T[],
  text: string
): T[] => {
  const query = text.trim().toLowerCase()
  const filtered = query
    ? list.filter(entity => entity.name.toLowerCase().includes(query))
    : list
  return filtered.slice(0, REFERENCED_ENTITY_SEARCH_LIMIT)
}

type ProjectSelectEntities = {
  divisions: ReferencedEntity[]
  businessClients: ReferencedEntity[]
  users: ReferencedEntityWithIcon[]
}
const EMPTY_ENTITIES: ProjectSelectEntities = {
  divisions: [],
  businessClients: [],
  users: [],
}

// 部門 / 顧客 / ユーザーの全件を一度だけ取得し、それらに対するオートコンプリート
// 検索関数を公開する。あわせてステータス / 採番形式の固定 enum を選択肢として返す。
export const useProjectSelectOptions = () => {
  const intl = useIntl()
  // 検索関数は取得完了を待ってから絞り込む（共有 Promise 方式）。
  // 取得完了前に検索が走ると空の候補を返したまま再検索されず、
  // 候補リストが表示されないままになるため。
  const entitiesPromiseRef = useRef<Promise<ProjectSelectEntities> | null>(null)
  const loadEntities = useCallback((): Promise<ProjectSelectEntities> => {
    entitiesPromiseRef.current ??= (async () => {
      try {
        const [divisions, businessClients, users] = await Promise.all([
          findDivisions(),
          findBusinessClients(),
          findUsers(),
        ])
        return {
          divisions: divisions.map(d => ({
            uuid: d.uuid,
            name: d.displayName,
          })),
          businessClients: businessClients.map(b => ({
            uuid: b.uuid,
            name: b.displayName,
          })),
          users: users.map(u => ({
            uuid: u.uuid,
            name: u.name,
            iconUrl: u.iconUrl,
          })),
        }
      } catch (err) {
        // 取得失敗時は選択肢を空で返し、次回の検索で再取得する。
        entitiesPromiseRef.current = null
        console.error('Failed to load project select options.', err)
        return EMPTY_ENTITIES
      }
    })()
    return entitiesPromiseRef.current
  }, [])

  useEffect(() => {
    // 初回検索を待たせないよう、マウント時に取得を開始しておく。
    loadEntities()
  }, [loadEntities])

  const searchDivision = useCallback(
    async (text: string): Promise<ReferencedEntity[]> =>
      filterByName((await loadEntities()).divisions, text),
    [loadEntities]
  )
  const searchBusinessClient = useCallback(
    async (text: string): Promise<ReferencedEntity[]> =>
      filterByName((await loadEntities()).businessClients, text),
    [loadEntities]
  )
  const searchProjectManager = useCallback(
    async (text: string): Promise<ReferencedEntityWithIcon[]> =>
      filterByName((await loadEntities()).users, text),
    [loadEntities]
  )

  const statusOptions = useMemo<SelectOption[]>(
    () => [
      {
        value: ProjectStatus.STANDBY,
        name: intl.formatMessage({ id: 'projectV2.status.standby' }),
      },
      {
        value: ProjectStatus.INPROGRESS,
        name: intl.formatMessage({ id: 'projectV2.status.inProgress' }),
      },
      {
        value: ProjectStatus.SUSPENDED,
        name: intl.formatMessage({ id: 'projectV2.status.suspended' }),
      },
      {
        value: ProjectStatus.COMPLETED,
        name: intl.formatMessage({ id: 'projectV2.status.completed' }),
      },
    ],
    [intl]
  )
  const wbsItemCodeFormatOptions = useMemo<SelectOption[]>(
    () => [
      {
        value: WbsItemCodeFormat.RANDOM,
        name: intl.formatMessage({ id: 'projectV2.wbsItemCodeFormat.random' }),
      },
      {
        value: WbsItemCodeFormat.SEQUENTIAL,
        name: intl.formatMessage({
          id: 'projectV2.wbsItemCodeFormat.sequential',
        }),
      },
    ],
    [intl]
  )

  return {
    searchDivision,
    searchBusinessClient,
    searchProjectManager,
    statusOptions,
    wbsItemCodeFormatOptions,
  }
}
